import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isDev = import.meta.env?.DEV ?? false;

      return (
        <div className="min-h-screen bg-flaeche-2 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-flaeche rounded-xl shadow-lg p-6 space-y-4">
            <div className="flex items-center gap-3 text-stoer">
              <AlertTriangle className="w-8 h-8" />
              <h1 className="text-xl font-bold">Fehler in der Anwendung</h1>
            </div>

            <p className="text-tinte-2">
              Bitte Seite neu laden. Falls der Fehler bleibt, Support informieren.
            </p>

            <div className="flex gap-3">
              <button
                onClick={this.handleReload}
                className="flex items-center gap-2 px-4 py-2 bg-akzent text-akzent-satz rounded-lg hover:bg-akzent transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Neu laden
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-4 py-2 bg-flaeche-2 text-tinte-2 rounded-lg hover:bg-linie transition-colors"
              >
                <Home className="w-4 h-4" />
                Zur Startseite
              </button>
            </div>

            {isDev && error && (
              <div className="mt-4 p-4 bg-flaeche-2 rounded-lg border border-linie overflow-auto">
                <p className="font-mono text-sm text-stoer font-semibold mb-2">
                  {error.name}: {error.message}
                </p>
                {error.stack && (
                  <pre className="text-xs text-stoer whitespace-pre-wrap break-words">
                    {error.stack}
                  </pre>
                )}
                {errorInfo?.componentStack && (
                  <pre className="mt-2 text-xs text-stoer whitespace-pre-wrap break-words">
                    {errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
